import { useEffect, useState, useRef } from 'react';
import styles from './styles/fixtures.module.css';
import imageData from '../../data/Services/FixturesData.json';
import FAB from '../../helpers/fab';

const useSwipe = (onSwipeLeft, onSwipeRight) => {
  const touchStartX = useRef(null);
  const touchEndX = useRef(null);
  const minSwipeDistance = 50;

  const onTouchStart = (e) => {
    touchStartX.current = e.changedTouches[0].screenX;
  };

  const onTouchEnd = (e) => {
    touchEndX.current = e.changedTouches[0].screenX;
    if (!touchStartX.current || !touchEndX.current) return;

    const distance = touchStartX.current - touchEndX.current;
    if (Math.abs(distance) > minSwipeDistance) {
      distance > 0 ? onSwipeLeft() : onSwipeRight();
    }
  };

  return { onTouchStart, onTouchEnd };
};

const Fixtures = () => {
  const [fixtureData, setFixtureData] = useState([]);
  const [activeTypeIndex, setActiveTypeIndex] = useState(0);
  const [activeSubTab, setActiveSubTab] = useState(null);
  const [modalState, setModalState] = useState({ images: [], index: null });

  const isMobile = window.innerWidth < 667;

  useEffect(() => {
    setFixtureData(imageData);
    // default subtab to first room of first type (if exists)
    if (imageData.length > 0) {
      const firstRooms = imageData[0]?.options?.map((o) => o.room);
      setActiveSubTab(firstRooms?.length > 0 ? firstRooms[0] : null);
    }
  }, []);

  const activeType = fixtureData[activeTypeIndex];

  // Rooms for the active type
  const subtabs = activeType?.options?.map((o) => o.room) || [];

  // Filter options based on selected room (subtab)
  const filteredOptions = activeSubTab
    ? activeType?.options.find((o) => o.room === activeSubTab)?.options || []
    : activeType?.options.flatMap((o) => o.options) || [];

  const openModal = (index) => {
    setModalState({ images: filteredOptions, index });
  };

  const closeModal = () => {
    setModalState({ images: [], index: null });
  };

  const prevImage = () => {
    setModalState((prev) => ({
      ...prev,
      index: prev.index === 0 ? prev.images.length - 1 : prev.index - 1,
    }));
  };

  const nextImage = () => {
    setModalState((prev) => ({
      ...prev,
      index: (prev.index + 1) % prev.images.length,
    }));
  };

  const isModalOpen = modalState.index !== null;
  const swipeHandlers = useSwipe(nextImage, prevImage);

  useEffect(() => {
    const handleKey = (e) => {
      if (!isModalOpen) return;
      if (e.key === 'ArrowLeft') prevImage();
      if (e.key === 'ArrowRight') nextImage();
      if (e.key === 'Escape') closeModal();
    };
    window.addEventListener('keydown', handleKey);
    return () => window.removeEventListener('keydown', handleKey);
  }, [isModalOpen]);

  return (
    <section className={styles.sinkAndFaucets}>
      <h2>Explore Our Sinks And Faucets</h2>
      <p className={styles.supplierLine}>
        <i>For a wider selection of high-quality sinks and faucets, visit our supplier, <a href="https://bristolsinks.com/" target="_blank" rel="noreferrer">Bristol Sinks Inc</a>.</i>
      </p>
      <div className={styles.sinkAndFaucetLayout}>
        <div className={styles.tabsContainer}>
          {/* LEFT SIDE: Main type tabs */}
          <aside className={styles.tabsNav}>
            {fixtureData.map((group, i) => (
              <button
                key={group.type}
                className={`${styles.tabButton} ${i === activeTypeIndex ? styles.active : ''}`}
                onClick={() => {
                  setActiveTypeIndex(i);
                  // Reset activeSubTab to first room of selected type
                  const firstRooms = fixtureData[i]?.options?.map((o) => o.room);
                  setActiveSubTab(firstRooms?.length > 0 ? firstRooms[0] : null);
                }}
              >
                {group.type}
              </button>
            ))}
          </aside>

          {/* RIGHT SIDE CONTENT */}
          <div className={styles.tabContent}>

            {isMobile && <div className={styles.typeDescriptionContainer}>
              <p className={styles.primaryDescription}>{activeType?.description}</p>
              {activeType?.subdescription !== null && (
                <p className={styles.secondaryDescription}>{activeType?.subdescription}</p>
              )}
            </div>}

            {/* Room subtabs inside right content */}
            {subtabs.length > 0 && (
              <div className={styles.subTabsWrapper} style={{ marginBottom: '1rem', display: 'flex', gap: '1rem' }}>
                {subtabs.map((room) => (
                  <button
                    key={room}
                    className={`${styles.subTab} ${room === activeSubTab ? styles.active : ''}`}
                    onClick={() => setActiveSubTab(room)}
                    style={{ cursor: 'pointer' }}
                  >
                    {room}
                  </button>
                ))}
              </div>
            )}

            {!isMobile && <div className={styles.typeDescriptionContainer}>
              <p className={styles.primaryDescription}>{activeType?.description}</p>
              {activeType?.subdescription !== null && (
                <p className={styles.secondaryDescription}>{activeType?.subdescription}</p>
              )}
            </div>}

            <div className={styles.sinkAndFaucetGallery}>
              {filteredOptions.map((option, idx) => (
                <div
                  className={styles.sinkAndFaucetCard}
                  key={idx}
                  onClick={() => openModal(idx)}
                >
                  <div
                    className={`${styles.sinkAndFaucetCardImage} ${
                      option.isBudgetFriendly ? styles.budgetFriendly : ''
                    }`}
                  >
                    {option.isBudgetFriendly && (
                      <span className={styles.budgetFriendlyText}>Budget Friendly</span>
                    )}
                    {option.isPopular && <span className={styles.popular}>Popular right now!</span>}
                    <img src={option.image} alt={option.name} />
                  </div>
                  <div className={styles.overlay}>{option.name}</div>
                  {option.isCanadian && (
                    <div className={styles.canadianBadge}>
                      <img
                        src="https://ik.imagekit.io/1stopkitchens/images/canadian_icon.png"
                        alt="Canadian"
                        className={styles.canadianIcon}
                      />
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* MODAL */}
      {isModalOpen && (
        <div className="modal-overlay" onClick={closeModal} {...swipeHandlers}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <button className="modal-close-btn" onClick={closeModal} aria-label="Close">
              ×
            </button>
            <p className="modal-image-caption">
              {modalState.images[modalState.index].name}
              {modalState.images[modalState.index].isCanadian && (
                <img
                  src="https://ik.imagekit.io/1stopkitchens/images/canadian_icon.png"
                  alt="Canadian"
                  className="canadian-icon"
                />
              )}
            </p>
            <div
              className={`modal-image-wrapper 
                ${modalState.images[modalState.index].isBudgetFriendly ? 'budget-friendly' : 'not-budget-friendly'}
                ${modalState.images[modalState.index].moreInfo ? 'has-zoom' : 'no-zoom'}
              `}
            >
              {modalState.images[modalState.index].isPopular && (
                <span className="modal-popular">Popular right now!</span>
              )}
              {modalState.images[modalState.index].isBudgetFriendly && (
                <span className="modal-budget-friendly-text">Budget Friendly</span>
              )}
              {modalState.images[modalState.index].moreInfo ? (
                <a
                  href={modalState.images[modalState.index].moreInfo}
                  target="_blank"
                  rel="noreferrer"
                  className="modal-link"
                >
                  <img
                    src={modalState.images[modalState.index].image}
                    alt={modalState.images[modalState.index].name}
                    className="modal-image"
                  />
                </a>
              ) : (
                <img
                  src={modalState.images[modalState.index].image}
                  alt={modalState.images[modalState.index].name}
                  className={`modal-image ${modalState.images[modalState.index].moreInfo ? 'has-more-info' : ''}`}
                />
              )}
            </div>
            {modalState.images[modalState.index].moreInfo && (
              <p className="more-info-text">
                <i>Click on the image above to see more information about this fixture</i>
              </p>
            )}
            <div className="modal-nav-buttons">
              <button className="nav-btn prev-btn" onClick={prevImage} aria-label="Previous image">
                ‹
              </button>
              <button className="nav-btn next-btn" onClick={nextImage} aria-label="Next image">
                ›
              </button>
            </div>
          </div>
        </div>
      )}

      {!isModalOpen && <FAB />}
    </section>
  );
};

export default Fixtures;